import type { ReactElement, ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface EmptyStateProps {
  /**
   * Ícone Tabler (sem wrapper). Componente recebe `size` interno —
   * EmptyState envolve num quadrado violet bg-violet/15 + ring-violet/30.
   */
  icon: ReactNode;
  /** Heading principal */
  title: string;
  /** Texto explicativo (1-2 linhas) */
  description: string;
  /** Ação principal (botão, link) */
  action?: ReactNode;
  /** Ação secundária opcional */
  secondaryAction?: ReactNode;
  className?: string;
}

/**
 * Empty state padrão Trakd · visual MCP-style:
 * - Glow violeta ambiental atrás
 * - Ícone grande em caixa bg-violet/15 com ring-violet/30
 * - Título + descrição centralizados
 * - Ação(ões) abaixo
 *
 * Usado em /anuncios, /pixels, /audiencias, /dashboard, /eventos,
 * /regras, /configuracoes/mcp, /audiencias.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  className,
}: EmptyStateProps): ReactElement {
  return (
    <div
      className={cn(
        'relative flex flex-col items-center justify-center gap-5 rounded-2xl px-6 py-12 text-center',
        className,
      )}
    >
      {/* Glow violeta de fundo */}
      <div
        aria-hidden
        className="bg-violet/20 pointer-events-none absolute top-1/2 left-1/2 h-48 w-48 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
      />

      {/* Ícone grande com anel violeta */}
      <div className="relative">
        <div aria-hidden className="bg-violet/15 absolute inset-0 rounded-2xl blur-md" />
        <span className="bg-violet/15 text-violet ring-violet/30 relative flex h-16 w-16 items-center justify-center rounded-2xl ring-1">
          {icon}
        </span>
      </div>

      <div className="relative max-w-md space-y-1.5">
        <h3 className="text-ink text-base font-semibold tracking-tight">{title}</h3>
        <p className="text-ink-muted text-sm leading-relaxed">{description}</p>
      </div>

      {action || secondaryAction ? (
        <div className="relative flex flex-wrap items-center justify-center gap-3">
          {action}
          {secondaryAction}
        </div>
      ) : null}
    </div>
  );
}
